import { useCallback, type Dispatch, type SetStateAction } from "react";
import type { GitStatusResult } from "@cmmd-forge/contracts";

import type {
  PendingDefaultBranchAction,
  RunGitActionWithToastInput,
} from "./GitActionsControl.runner";

type GitChangedFile = GitStatusResult["workingTree"]["files"][number];

interface UseGitCommitDialogActionsInput {
  readonly isCommitDialogOpen: boolean;
  readonly dialogCommitMessage: string;
  readonly allSelected: boolean;
  readonly selectedFiles: readonly GitChangedFile[];
  readonly pendingDefaultBranchAction: PendingDefaultBranchAction | null;
  readonly runGitActionWithToast: (input: RunGitActionWithToastInput) => Promise<void>;
  readonly setIsCommitDialogOpen: Dispatch<SetStateAction<boolean>>;
  readonly setDialogCommitMessage: Dispatch<SetStateAction<string>>;
  readonly setExcludedFiles: Dispatch<SetStateAction<ReadonlySet<string>>>;
  readonly setIsEditingFiles: Dispatch<SetStateAction<boolean>>;
  readonly setPendingDefaultBranchAction: Dispatch<
    SetStateAction<PendingDefaultBranchAction | null>
  >;
}

export function useGitCommitDialogActions(input: UseGitCommitDialogActionsInput) {
  const resetCommitDialog = useCallback(() => {
    input.setIsCommitDialogOpen(false);
    input.setDialogCommitMessage("");
    input.setExcludedFiles(new Set());
    input.setIsEditingFiles(false);
  }, [input]);

  const continuePendingDefaultBranchAction = useCallback(() => {
    const pending = input.pendingDefaultBranchAction;
    if (!pending) return;
    input.setPendingDefaultBranchAction(null);
    void input.runGitActionWithToast(toPendingRunInput(pending, false));
  }, [input]);

  const checkoutFeatureBranchAndContinuePendingAction = useCallback(() => {
    const pending = input.pendingDefaultBranchAction;
    if (!pending) return;
    input.setPendingDefaultBranchAction(null);
    void input.runGitActionWithToast(toPendingRunInput(pending, true));
  }, [input]);

  const runDialogActionOnNewBranch = useCallback(() => {
    if (!input.isCommitDialogOpen) return;
    const commitMessage = input.dialogCommitMessage.trim();
    resetCommitDialog();
    void input.runGitActionWithToast({
      action: "commit",
      ...(commitMessage ? { commitMessage } : {}),
      ...(!input.allSelected ? { filePaths: input.selectedFiles.map((file) => file.path) } : {}),
      featureBranch: true,
      skipDefaultBranchPrompt: true,
    });
  }, [input, resetCommitDialog]);

  const runDialogAction = useCallback(() => {
    if (!input.isCommitDialogOpen) return;
    const commitMessage = input.dialogCommitMessage.trim();
    resetCommitDialog();
    void input.runGitActionWithToast({
      action: "commit",
      ...(commitMessage ? { commitMessage } : {}),
      ...(!input.allSelected ? { filePaths: input.selectedFiles.map((file) => file.path) } : {}),
    });
  }, [input, resetCommitDialog]);

  return {
    resetCommitDialog,
    continuePendingDefaultBranchAction,
    checkoutFeatureBranchAndContinuePendingAction,
    runDialogActionOnNewBranch,
    runDialogAction,
  };
}

function toPendingRunInput(
  pending: PendingDefaultBranchAction,
  featureBranch: boolean,
): RunGitActionWithToastInput {
  return {
    action: pending.action,
    ...(pending.commitMessage ? { commitMessage: pending.commitMessage } : {}),
    ...(pending.onConfirmed ? { onConfirmed: pending.onConfirmed } : {}),
    ...(pending.filePaths ? { filePaths: pending.filePaths } : {}),
    ...(featureBranch ? { featureBranch: true } : {}),
    skipDefaultBranchPrompt: true,
  };
}
